import { makeid } from 'utils/helpers/makeid';
import { ReadingType } from 'infrastructure/enums';
import classNames from 'classnames';

import s from '../styles.module.scss';

import type { ReadingStatus } from 'infrastructure/enums';
import type { TFormItem, TFormsReadingType } from '../types';

export type TUniqueDaysType = 'blood pressure' | 'SpO2' | 'glucose' | 'weight';

export const uniqueDaysItem = (type: TUniqueDaysType): TFormItem => ({
  key: makeid(10),
  title: (
    <p>
      How many unique days this week did the patient have {type} readings?{' '}
      <span>__ days</span>
    </p>
  ),
  type: 'input-number',
  required: false,
  name: 'uniqueDays',
  validation: {
    min: 0,
    max: 7,
  },
});

export const twoWaySyncInfoItem: TFormItem = {
  key: makeid(10),
  html: <p>TWO-WAY SYNC PHONE CALL</p>,
  type: 'info',
  name: '-',
};

export const isPatientCROorDNCItem: TFormItem = {
  key: makeid(10),
  title: (
    <p>
      Is this patient a <b>CRO</b> or <b>DNC</b> patient?
      <br />
      <br />
    </p>
  ),
  type: 'empty',
  name: '-',
  items: [
    {
      key: makeid(10),
      type: 'radio',
      name: 'completedPhoneCall',
      required: false,
      title: (
        <p>
          If NO: Has a two-way sync (completed phone call with the patient)
          occurred this month?
        </p>
      ),
      options: [
        { label: 'Yes', value: true, key: makeid(10) },
        { label: 'No', value: false, key: makeid(10) },
      ],
    },
    {
      key: makeid(10),
      type: 'radio',
      name: 'attemptedPhoneCall',
      required: false,
      title: (
        <p>
          If NO: Attempt to call the patient now. Was a two-way sync (phone
          call) completed during this review?
        </p>
      ),
      options: [
        {
          label: 'Yes, phone call completed — patient reached',
          value: 'yes',
          key: makeid(10),
        },
        {
          label:
            'No, phone call attempted but patient not reached/voicemail left',
          value: 'no-reached',
          key: makeid(10),
        },
        {
          label: 'No, phone call not attempted (provide reason in free text)',
          value: 'no-reason',
          key: makeid(10),
        },
      ],
      items: [
        {
          type: 'textarea',
          name: 'attemptedPhoneCallComment',
          title: null,
          key: makeid(10),
        },
      ],
    },
  ],
};

export const additionalFollowUpItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  name: 'additionalFollowUp',
  required: false,
  title: (
    <p>
      Does this patient require any additional follow-up regarding two-way sync
      (e.g., further call attempts or to schedule a call)?
    </p>
  ),
  options: [
    {
      label: 'Yes, additional follow-up needed (describe in free text)',
      value: true,
      key: makeid(10),
    },
    {
      label: 'No additional follow-up required',
      value: false,
      key: makeid(10),
    },
  ],
  items: [
    {
      type: 'textarea',
      name: 'additionalFollowUpComment',
      title: null,
      key: makeid(10),
    },
  ],
};

export const needEscalateItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  name: 'needEscalate',
  required: false,
  title: (
    <p>
      Do you need to escalate this case to a nurse based on abnormal readings or
      protocol?
    </p>
  ),
  options: [
    {
      label: 'Yes (specify reason in free text and notify nurse)',
      value: true,
      key: makeid(10),
    },
    {
      label: 'No',
      value: false,
      key: makeid(10),
    },
  ],
  items: [
    {
      type: 'textarea',
      name: 'needEscalateComment',
      title: null,
      key: makeid(10),
    },
  ],
};

export const freeTextCommentsItem: TFormItem = {
  key: makeid(10),
  type: 'textarea',
  name: 'freeTextComments',
  title: (
    <p>
      Free Text Comments (context or protocol notes; no clinical assessment):
    </p>
  ),
};

const modalTitles = {
  [ReadingType.BloodPressure]: {
    title: 'Blood Pressure Monitoring',
    subTitle: '(Systolic, Diastolic, Pulse)',
  },
  [ReadingType.BloodGlucose]: {
    title: 'Glucose Monitoring',
    subTitle: undefined,
  },
  [ReadingType.PeakFlow]: {
    title: 'Peak Flow',
    subTitle: undefined,
  },
  [ReadingType.SleepingMat]: {
    title: 'Sleeping Mat',
    subTitle: undefined,
  },
  [ReadingType.Temperature]: {
    title: 'Temperature',
    subTitle: undefined,
  },
  [ReadingType.Weight]: {
    title: 'Weight Monitoring',
    subTitle: undefined,
  },
  [ReadingType.SpO2]: {
    title: 'SpO2 Monitoring',
    subTitle: '(SpO2%, Pulse)',
  },
  [ReadingType.Pulse]: {
    title: 'Pulse',
    subTitle: undefined,
  },
};

export const genReadingInfoItem = (
  readingType: TFormsReadingType,
  status: ReadingStatus,
): TFormItem => ({
  key: makeid(10),
  type: 'info',
  html: (
    <div className={classNames(s['reading-type-item-wrapper'], s[status])}>
      <p className={s['reading-type-title']}>
        {modalTitles[readingType].title}
      </p>
      {modalTitles[readingType].subTitle && (
        <p className={s['reading-type-subtitle']}>
          {modalTitles[readingType].subTitle}
        </p>
      )}
    </div>
  ),
  name: '_info',
});

export const freeTextTemplate = (
  readingType: TFormsReadingType,
): TFormItem[] => [
  {
    key: makeid(10),
    title: <p>Free Text</p>,
    type: 'textarea',
    required: false,
    name: readingType,
  },
];

export const nurseContactWithPatientItem: TFormItem = {
  key: makeid(10),
  title: <p>Contact with patient:</p>,
  type: 'radio',
  name: 'contactWithPatient',
  required: false,
  options: [
    {
      label: 'Phone call completed',
      value: true,
      key: makeid(10),
    },
    {
      label: 'Phone call attempted, unable to reach/left voicemail',
      value: false,
      key: makeid(10),
    },
  ],
};

export const nurseNoCallMadeRationaleItem: TFormItem = {
  key: makeid(10),
  title: <p>No call made—rationale:</p>,
  type: 'radio',
  name: 'noCallMadeRationale',
  required: false,
  options: [
    {
      label: 'Trend or reading resolved/abated',
      value: 'trend or reading resolved/abated',
      key: makeid(10),
    },
    {
      label: 'Recent call already performed',
      value: 'recent call already performed',
      key: makeid(10),
    },
    {
      label: 'No patient phone number',
      value: 'no patient phone number',
      key: makeid(10),
    },
    {
      label: 'Provider instructed not to call',
      value: 'provider instructed not to call',
      key: makeid(10),
    },
    {
      label: 'Other (describe below)',
      value: 'other',
      key: makeid(10),
    },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'noCallMadeOtherComment',
    },
  ],
};

export const nursePatientAcknowledgementItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  required: false,
  name: 'patientAcknowledgement',
  title: (
    <p>
      Patient Acknowledgment. Was patient able to verbalize understanding of
      education provided?
    </p>
  ),
  options: [
    {
      label: 'Yes',
      value: 'yes',
      key: makeid(10),
    },
    {
      label: 'No',
      value: 'no',
      key: makeid(10),
    },
    {
      label: 'Partially',
      value: 'partially',
      key: makeid(10),
    },
  ],
};

export const nursePatientAgreeabilityItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  required: false,
  title: <p>Was patient agreeable to the nursing recommendation:</p>,
  name: 'patientAgreeability',
  options: [
    {
      label: 'Yes',
      value: 'yes',
      key: makeid(10),
    },
    {
      label: 'No',
      value: 'no',
      key: makeid(10),
    },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'patientAgreeabilityComment',
    },
  ],
};

export const nurseMaTasksDelegatedItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  title: <p>MA tasks delegated:</p>,
  name: 'maTasksDelegated',
  required: false,
  options: [
    {
      label: 'Yes',
      value: 'yes',
      key: makeid(10),
      hideSubitemsIfParentNotSelected: true,
      subItems: [
        {
          key: makeid(10),
          title: null,
          type: 'checkbox',
          name: 'maTasksDelegatedList',
          options: [
            {
              key: makeid(10),
              label: 'Device troubleshooting',
              value: 'device troubleshooting',
            },
            {
              key: makeid(10),
              label:
                'Social Determinants of Health support (check all that apply)',
              value: 'social Determinants of Health support',
            },
            {
              key: makeid(10),
              label: 'Transportation to clinic or pharmacy',
              value: 'transportation to clinic or pharmacy',
            },
            {
              key: makeid(10),
              label: 'Medication or medical supply affordability',
              value: 'medication or medical supply affordability',
            },
            {
              key: makeid(10),
              label: 'Pharmacy access (location, delivery, 24 hr)',
              value: 'pharmacy access (location, delivery, 24 hr)',
            },
            {
              key: makeid(10),
              label: 'Food insecurity/nutrition support',
              value: 'food insecurity/nutrition support',
            },
            {
              key: makeid(10),
              label: 'Housing insecurity or homelessness',
              value: 'housing insecurity or homelessness',
            },
            {
              key: makeid(10),
              label:
                'Utility or energy assistance (e.g., electricity for devices)',
              value:
                'utility or energy assistance (e.g., electricity for devices)',
            },
            {
              key: makeid(10),
              label: 'Health insurance issues or resources',
              value: 'health insurance issues or resources',
            },
            {
              key: makeid(10),
              label: 'Difficulty with ADLs (activities of daily living)',
              value: 'difficulty with ADLs (activities of daily living)',
            },
            {
              key: makeid(10),
              label: 'Language/interpretation services',
              value: 'language/interpretation services',
            },
            {
              key: makeid(10),
              label: 'Cognitive or memory support for care',
              value: 'cognitive or memory support for care',
            },
            {
              key: makeid(10),
              label: 'Caregiver support or respite needs',
              value: 'caregiver support or respite needs',
            },
            {
              key: makeid(10),
              label:
                'Emotional/mental health resources (e.g., social work, counseling)',
              value:
                'emotional/mental health resources (e.g., social work, counseling)',
            },
            {
              key: makeid(10),
              label: 'Legal, documentation, or immigration assistance',
              value: 'legal, documentation, or immigration assistance',
            },
            {
              key: makeid(10),
              label: 'Financial assistance/poverty resources',
              value: 'financial assistance/poverty resources',
            },
            {
              key: makeid(10),
              label: 'Domestic abuse/safety concern',
              value: 'domestic abuse/safety concern',
            },
            {
              key: makeid(10),
              label: 'Connection to community programs or benefits',
              value: 'connection to community programs or benefits',
            },
          ],
        },
        {
          key: makeid(10),
          title: null,
          type: 'textarea',
          name: 'maTasksDelegatedListComment',
        },
      ],
    },
    {
      label: 'None at this time',
      value: 'no',
      key: makeid(10),
    },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'maTasksDelegatedComment',
    },
  ],
};

export const nurseReviewEscalationProtocolItem: TFormItem = {
  key: makeid(10),
  title: <p>Review escalation protocol:</p>,
  type: 'radio',
  required: false,
  name: 'reviewEscalationProtocol',
  options: [
    { label: 'Yes', value: true, key: makeid(10) },
    { label: 'No (reason)', value: false, key: makeid(10) },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'reviewEscalationProtocolComment',
    },
  ],
};

export const nurseEmergencyCareAdvisedItem: TFormItem = {
  key: makeid(10),
  title: <p>Emergency care advised: </p>,
  type: 'radio',
  required: false,
  name: 'emergencyCareAdvised',
  options: [
    { label: 'Yes', value: true, key: makeid(10) },
    { label: 'No (reason)', value: false, key: makeid(10) },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'emergencyCareAdvisedComment',
    },
  ],
};

export const nurseDecisionMakingNextStepsItem: TFormItem = {
  key: makeid(10),
  type: 'checkbox',
  title: <p>Decision making & next steps:</p>,
  required: false,
  name: 'decisionMakingNextSteps',
  options: [
    {
      label: 'Continue monitoring',
      value: 'continue monitoring',
      key: makeid(10),
    },
    {
      label: 'Escalate to provider (reason)',
      value: 'escalate to provider <escalateToProviderReason>',
      key: makeid(10),
      hideSubitemsIfParentNotSelected: true,
      subItems: [
        {
          key: makeid(10),
          title: null,
          type: 'input',
          name: 'escalateToProviderReason',
        },
      ],
    },
    {
      label: 'Notify oxygen supplier/pharmacy (describe)',
      value: 'notify oxygen supplier/pharmacy <notifyPharmacyReason>',
      key: makeid(10),
      hideSubitemsIfParentNotSelected: true,
      subItems: [
        {
          key: makeid(10),
          title: null,
          type: 'input',
          name: 'notifyPharmacyReason',
        },
      ],
    },
    {
      label: 'Urgent/emergency referral made',
      value: 'urgent/emergency referral made',
      key: makeid(10),
    },
    {
      label: 'Schedule follow-up call in ___ days/weeks',
      value: 'schedule follow-up call in <value> days/weeks',
      key: makeid(10),
      hideSubitemsIfParentNotSelected: true,
      subItems: [
        {
          key: makeid(10),
          title: null,
          type: 'input-number',
          name: 'scheduleFollowUpCallValue',
          validation: {
            min: 0,
          },
        },
      ],
    },
    {
      label: 'Plan for MA assistance (see below)',
      value: 'plan for MA assistance (see below)',
      key: makeid(10),
    },
  ],
};

export const nurseDigitalEngagementEducationMethodItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  title: <p>Digital Engagement Education method:</p>,
  name: 'digitalEngagementEducationMethod',
  required: false,
  options: [
    {
      label: 'Phone',
      value: 'phone',
      key: makeid(10),
    },
    {
      label: 'SMS/text',
      value: 'SMS/text',
      key: makeid(10),
    },
    {
      label: 'Email',
      value: 'email',
      key: makeid(10),
    },
    {
      label: 'US Mail',
      value: 'US mail',
      key: makeid(10),
    },
  ],
  items: [
    {
      key: makeid(10),
      title: null,
      type: 'textarea',
      name: 'digitalEngagementEducationMethodComment',
    },
  ],
};

export const nurseFollowUpPlanItem: TFormItem = {
  key: makeid(10),
  type: 'radio',
  title: <p>Follow-up plan:</p>,
  name: 'followUpPlan',
  required: false,
  options: [
    {
      label: 'Yes',
      value: 'yes',
      key: makeid(10),
      hideSubitemsIfParentNotSelected: true,
      subItems: [
        {
          key: makeid(10),
          title: null,
          type: 'input-date',
          name: 'followUpPlanDate',
        },
      ],
    },
    {
      label: 'No',
      value: 'no',
      key: makeid(10),
    },
  ],
};
